// Pollen scoring + display helpers (client side mirrors of server types).

export const POLLEN_KEYS = [
  "alder_pollen",
  "birch_pollen",
  "grass_pollen",
  "mugwort_pollen",
  "olive_pollen",
  "ragweed_pollen",
] as const;
export type PollenKey = (typeof POLLEN_KEYS)[number];

export interface PollenSnapshot {
  available: boolean;
  units: string;
  current: Record<PollenKey, number | null>;
  daily: { date: string; values: Record<PollenKey, number | null> }[];
}

export const POLLEN_LABELS: Record<PollenKey, { label: string; emoji: string }> = {
  alder_pollen: { label: "Alder", emoji: "🌳" },
  birch_pollen: { label: "Birch", emoji: "🌲" },
  grass_pollen: { label: "Grass", emoji: "🌾" },
  mugwort_pollen: { label: "Mugwort", emoji: "🌿" },
  olive_pollen: { label: "Olive", emoji: "🫒" },
  ragweed_pollen: { label: "Ragweed", emoji: "🌱" },
};

// CAMS pollen thresholds (grains/m³). These are commonly cited cutoffs.
const THRESHOLDS: Record<PollenKey, { low: number; medium: number; high: number }> = {
  alder_pollen: { low: 1, medium: 30, high: 100 },
  birch_pollen: { low: 1, medium: 30, high: 100 },
  grass_pollen: { low: 1, medium: 20, high: 50 },
  mugwort_pollen: { low: 1, medium: 10, high: 50 },
  olive_pollen: { low: 1, medium: 30, high: 100 },
  ragweed_pollen: { low: 1, medium: 10, high: 50 },
};

export type PollenLevel = "none" | "low" | "medium" | "high" | "very-high";

export function pollenLevel(key: PollenKey, value: number | null | undefined): PollenLevel {
  if (value == null) return "none";
  const t = THRESHOLDS[key];
  if (value < t.low) return "none";
  if (value < t.medium) return "low";
  if (value < t.high) return "medium";
  if (value < t.high * 2) return "high";
  return "very-high";
}

export function pollenLevelColor(lvl: PollenLevel): { bar: string; text: string; bg: string } {
  switch (lvl) {
    case "none":
      return { bar: "bg-emerald-500/40", text: "text-emerald-400", bg: "bg-emerald-500/10" };
    case "low":
      return { bar: "bg-emerald-400", text: "text-emerald-400", bg: "bg-emerald-500/15" };
    case "medium":
      return { bar: "bg-amber-400", text: "text-amber-400", bg: "bg-amber-500/15" };
    case "high":
      return { bar: "bg-orange-400", text: "text-orange-400", bg: "bg-orange-500/15" };
    case "very-high":
      return { bar: "bg-rose-400", text: "text-rose-400", bg: "bg-rose-500/15" };
  }
}

export interface PollenOverall {
  level: PollenLevel;
  score: number; // 0-100, higher = worse
  topAllergen: { key: PollenKey; level: PollenLevel; value: number } | null;
}

export function summarizePollen(current: Record<PollenKey, number | null>): PollenOverall {
  const levels: { key: PollenKey; level: PollenLevel; value: number }[] = [];
  for (const key of POLLEN_KEYS) {
    const v = current[key];
    if (v == null || v < 0) continue;
    const lvl = pollenLevel(key, v);
    levels.push({ key, level: lvl, value: v });
  }
  if (levels.length === 0) return { level: "none", score: 0, topAllergen: null };

  const rank: Record<PollenLevel, number> = { none: 0, low: 1, medium: 2, high: 3, "very-high": 4 };
  const top = levels.reduce((a, b) => (rank[b.level] > rank[a.level] ? b : a));
  // Score: weighted highest level + count of moderate+ allergens
  const score = Math.min(
    100,
    rank[top.level] * 25 + levels.filter((l) => rank[l.level] >= 2).length * 5,
  );
  return { level: top.level, score, topAllergen: top };
}
